import { Component, ChangeDetectionStrategy, input, output, computed, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

// Kendo UI Modules
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { IconsModule, SVGIcon } from '@progress/kendo-angular-icons';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { NotificationService } from '@progress/kendo-angular-notification';

// Icons
import {
  pencilIcon,
  trashIcon,
  infoCircleIcon,
  gearIcon,
  securityCheckOutlineIcon,
  saveIcon,
  userIcon,
  clockIcon,
  exclamationCircleIcon,
} from '@progress/kendo-svg-icons';

// Models
import { ServiceInfo, ServiceType } from '../../models/customer-details.model';

// Base Component
import { BaseComponent } from 'src/app/core/shared/common/base.component';

export interface ServiceWidgetData {
  service: ServiceInfo;
  onModify: (service: ServiceInfo) => void;
  onCancel: (service: ServiceInfo) => void;
}

export interface ServiceCancellationEvent {
  service: ServiceInfo;
  cancellationReason: string;
}

@Component({
  selector: 'app-service-widget',
  standalone: true,
  templateUrl: './service-widget.component.html',
  styleUrls: ['./service-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ButtonsModule,
    IconsModule,
    LayoutModule,
    DialogsModule,
    InputsModule,
    LabelModule,
    ReactiveFormsModule,
  ],
  providers: [NotificationService],
})
export class ServiceWidgetComponent extends BaseComponent {
  // Services
  private readonly fb = inject(FormBuilder);
  private readonly notificationService = inject(NotificationService);

  // Input/Output
  public service = input.required<ServiceInfo>();
  public onModify = output<ServiceInfo>();
  public onCancel = output<ServiceCancellationEvent>();

  // Dialog state
  public isCancelDialogOpen = signal(false);
  public cancelForm: FormGroup;

  // Kendo UI SVG Icons
  public editIcon = pencilIcon;
  public trashIcon = trashIcon;
  public infoCircleIcon = infoCircleIcon;
  public gearIcon = gearIcon;
  public shieldCheckIcon = securityCheckOutlineIcon;
  public serverIcon = saveIcon;
  public networkIcon = gearIcon;
  public userIcon = userIcon;
  public clockIcon = clockIcon;
  public saveIcon = saveIcon;
  public exclamationCircleIcon = exclamationCircleIcon;

  constructor() {
    super();
    this.cancelForm = this.createCancelForm();
  }

  private createCancelForm(): FormGroup {
    return this.fb.group({
      reason: [''],
    });
  }

  // Computed values
  public serviceIcon = computed(() => {
    const serviceType = this.service().serviceType;
    switch (serviceType) {
      case ServiceType.MXDR:
        return this.shieldCheckIcon;
      case ServiceType.MEDR:
        return this.serverIcon;
      case ServiceType.VSS:
        return this.gearIcon;
      case ServiceType.MES:
        return this.networkIcon;
      case ServiceType.PROFESSIONAL_SERVICES:
        return this.userIcon;
      default:
        return this.gearIcon;
    }
  });

  public serviceTitle = computed(() => {
    const serviceType = this.service().serviceType;
    switch (serviceType) {
      case ServiceType.MXDR:
        return 'mXDR Service';
      case ServiceType.MEDR:
        return 'mEDR Service';
      case ServiceType.VSS:
        return 'VSS Service';
      case ServiceType.MES:
        return 'mES Service';
      case ServiceType.PROFESSIONAL_SERVICES:
        return 'Professional Services';
      default:
        return 'Service';
    }
  });

  public serviceSubtitle = computed(() => {
    const serviceType = this.service().serviceType;
    switch (serviceType) {
      case ServiceType.MXDR:
        return 'Extended Detection & Response';
      case ServiceType.MEDR:
        return 'Managed Endpoint Detection & Response';
      case ServiceType.VSS:
        return 'Vulnerability Scanning Service';
      case ServiceType.MES:
        return 'Managed Email Security';
      case ServiceType.PROFESSIONAL_SERVICES:
        return 'Professional Security Services';
      default:
        return 'Security Service';
    }
  });

  public statusBadgeClass = computed(() => {
    const status = this.service().status;
    switch (status) {
      case 'active':
        return 'bg-success';
      case 'inactive':
        return 'bg-secondary';
      case 'pending':
        return 'bg-warning';
      default:
        return 'bg-secondary';
    }
  });

  public statusText = computed(() => {
    const status = this.service().status;
    switch (status) {
      case 'active':
        return 'Active';
      case 'inactive':
        return 'Inactive';
      case 'pending':
        return 'Pending';
      default:
        return status;
    }
  });

  public handleModify(): void {
    this.onModify.emit(this.service());
  }

  public handleCancel(): void {
    this.isCancelDialogOpen.set(true);
  }

  public openCancelDialog(): void {
    this.isCancelDialogOpen.set(true);
  }

  public closeCancelDialog(): void {
    this.isCancelDialogOpen.set(false);
    this.cancelForm.reset();
  }

  public confirmCancel(): void {
    if (this.cancelForm.valid) {
      const reason = this.cancelForm.value.reason;

      // Show success notification with auto-close
      this.notificationService.show({
        content: `Service "${this.service().serviceName}" has been cancelled successfully.`,
        type: { style: 'success' },
        position: { horizontal: 'right', vertical: 'top' },
        closable: false,
        hideAfter: 5000, // Auto-close after 5 seconds
      });

      // Emit the cancellation event with reason
      this.onCancel.emit({
        service: this.service(),
        cancellationReason: reason,
      });

      // Close dialog and reset form
      this.closeCancelDialog();
    }
  }

  public getServiceDetails(): any[] {
    const service = this.service();
    const details: any[] = [];

    // Common details
    details.push({
      label: 'Status',
      value: this.statusText(),
      type: 'badge',
      badgeClass: this.statusBadgeClass(),
    });

    details.push({
      label: 'Start Date',
      value: service.startDate,
      type: 'date',
    });

    if (service.endDate) {
      details.push({
        label: 'End Date',
        value: service.endDate,
        type: 'date',
      });
    }

    // Service-specific details
    switch (service.serviceType) {
      case ServiceType.MXDR:
        const mxdrService = service as any;
        if (mxdrService.serviceSku) {
          details.push({
            label: 'Service SKU',
            value: mxdrService.serviceSku,
            type: 'text',
          });
        }
        if (mxdrService.mxdrType) {
          details.push({
            label: 'Service Type',
            value: this.getMxdrTypeDisplay(mxdrService.mxdrType),
            type: 'text',
          });
        }
        if (mxdrService.quantityAdUsers) {
          details.push({
            label: 'AD Users',
            value: mxdrService.quantityAdUsers,
            type: 'number',
          });
        }
        if (mxdrService.quantityServers) {
          details.push({
            label: 'Servers',
            value: mxdrService.quantityServers,
            type: 'number',
          });
        }
        if (mxdrService.quantityAppliances) {
          details.push({
            label: 'Appliances',
            value: mxdrService.quantityAppliances,
            type: 'number',
          });
        }
        if (mxdrService.govCloud) {
          details.push({
            label: 'Gov Cloud',
            value: 'Yes',
            type: 'text',
          });
        }
        if (mxdrService.extendedLogRetention) {
          details.push({
            label: 'Extended Log Retention',
            value: 'Yes',
            type: 'text',
          });
        }
        if (mxdrService.virtualAppliance) {
          details.push({
            label: 'Virtual Appliance',
            value: 'Yes',
            type: 'text',
          });
        }
        break;

      case ServiceType.MEDR:
        const medrService = service as any;
        if (medrService.slaLevel) {
          details.push({
            label: 'SLA Level',
            value: this.getSlaLevelDisplay(medrService.slaLevel),
            type: 'text',
          });
        }
        if (medrService.totalMachines) {
          details.push({
            label: 'Total Machines',
            value: medrService.totalMachines,
            type: 'number',
          });
        }
        if (medrService.medrConnect) {
          details.push({
            label: 'mEDR Connect',
            value: 'Enabled',
            type: 'text',
          });
        }
        if (medrService.medrComplete) {
          details.push({
            label: 'mEDR Complete',
            value: 'Enabled',
            type: 'text',
          });
        }
        break;

      case ServiceType.VSS:
        const vssService = service as any;
        if (vssService.totalMachines) {
          details.push({
            label: 'Total Machines',
            value: vssService.totalMachines,
            type: 'number',
          });
        }
        if (vssService.vssEssential) {
          details.push({
            label: 'VSS Essential',
            value: 'Enabled',
            type: 'text',
          });
        }
        if (vssService.vssEnhanced) {
          details.push({
            label: 'VSS Enhanced',
            value: 'Enabled',
            type: 'text',
          });
        }
        break;

      case ServiceType.MES:
        const mesService = service as any;
        if (mesService.slaLevel) {
          details.push({
            label: 'SLA Level',
            value: this.getSlaLevelDisplay(mesService.slaLevel),
            type: 'text',
          });
        }
        if (mesService.mailboxes) {
          details.push({
            label: 'Mailboxes',
            value: mesService.mailboxes,
            type: 'number',
          });
        }
        if (mesService.domainName) {
          details.push({
            label: 'Domain',
            value: mesService.domainName,
            type: 'text',
          });
        }
        break;

      case ServiceType.PROFESSIONAL_SERVICES:
        const psService = service as any;
        const enabledServices = [];
        if (psService.penetrationTesting) enabledServices.push('Penetration Testing');
        if (psService.incidentResponse) enabledServices.push('Incident Response');
        if (psService.vciso) enabledServices.push('vCISO');
        if (psService.attackSurfaceAssessment) enabledServices.push('Attack Surface Assessment');
        if (psService.breachAttackAssessment) enabledServices.push('Breach Attack Assessment');

        if (enabledServices.length > 0) {
          details.push({
            label: 'Services',
            value: enabledServices.join(', '),
            type: 'text',
          });
        }
        break;
    }

    return details;
  }

  private getMxdrTypeDisplay(type: string): string {
    switch (type) {
      case 'connect':
        return 'mXDR Connect';
      case 'complete':
        return 'mXDR Complete';
      case 'office_365':
        return 'mXDR 365';
      default:
        return type;
    }
  }

  private getSlaLevelDisplay(level: string): string {
    switch (level) {
      case 'advanced':
        return 'Advanced';
      case 'premier':
        return 'Premier';
      default:
        return level;
    }
  }
}
